import React, { useState } from "react";
import { Sparkles, Send, AlertTriangle, MessageCircle, ShieldCheck } from "lucide-react";
import { askTutor, trackEvent } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const STARTERS = [
  "Why is this correct?",
  "Why are the other choices wrong?",
  "Explain it more simply",
  "What's the Illinois rule here?",
];

export default function TutorPanel({ questionId }) {
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [mode, setMode] = useState("built_in");
  const [capMsg, setCapMsg] = useState(null);

  const blocked = Boolean(capMsg);

  const quotaText = capMsg
    ? "0 left today"
    : mode === "built_in"
    ? "Built-in coach"
    : usage
    ? `${Math.max(0, usage.daily_cap - usage.daily_used)} of ${usage.daily_cap} left today`
    : "AI enhanced";

  const send = async (raw) => {
    const message = (raw ?? input).trim();
    if (!message || busy || blocked) return;

    setThread((t) => [...t, { role: "user", text: message }]);
    setInput("");
    setBusy(true);
    trackEvent("tutor_ask", { question_id: questionId, message: message.slice(0, 200) });

    try {
      const data = await askTutor(questionId, message);
      setMode(data?.mode === "ai" ? "ai" : "built_in");
      setThread((t) => [...t, { role: "assistant", text: (data && data.reply) || "" }]);
      if (data && data.usage) setUsage(data.usage);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setCapMsg(
          detail || "You've reached today's AI Question Coach limit. Please try again tomorrow."
        );
        // pop the just-added user message back into the box so it isn't lost
        setThread((t) => t.slice(0, -1));
        setInput(message);
      } else {
        setThread((t) => [
          ...t,
          {
            role: "assistant",
            text: "Sorry, the tutor is temporarily unavailable. Please try again in a moment.",
          },
        ]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={STUDY.tutorPanel}
      className="mt-5 overflow-hidden rounded-lg border border-[color:rgba(10,27,51,0.14)] bg-white shadow-[0_12px_32px_rgba(7,20,39,0.08)]"
    >
      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[var(--ibp-ink)]">
              {mode === "ai" ? "AI Question Coach" : "Question Coach"}
            </h3>
            <p className="mt-0.5 text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Grounded in this question, its reviewed explanation, and source.
            </p>
          </div>
        </div>
        <span
          data-testid={STUDY.tutorQuota}
          className={[
            "w-fit shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            capMsg
              ? "bg-[color:rgba(180,35,24,0.1)] text-[#B42318]"
              : mode === "ai"
              ? "bg-[color:rgba(31,138,91,0.1)] text-[#1F7A52]"
              : "bg-[var(--ibp-offwhite)] text-[color:rgba(11,18,32,0.65)]",
          ].join(" ")}
        >
          {quotaText}
        </span>
      </div>
      <div className="border-t border-[color:rgba(10,27,51,0.1)] bg-[#f7f8fa] p-4 sm:p-5">
        {/* Conversation */}
        {thread.length > 0 ? (
          <div className="space-y-3" aria-live="polite">
            {thread.map((m, idx) => (
              <div
                key={idx}
                className={m.role === "user" ? "flex justify-end" : "flex items-start gap-2.5"}
              >
                {m.role === "assistant" ? (
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[#B7791F] shadow-sm ring-1 ring-[color:rgba(10,27,51,0.1)]">
                    <Sparkles className="h-3.5 w-3.5" />
                  </span>
                ) : null}
                <div
                  data-testid={m.role === "assistant" ? STUDY.tutorReply : undefined}
                  className={[
                    "max-w-[88%] rounded-xl px-3.5 py-2.5 text-sm leading-6",
                    m.role === "user"
                      ? "bg-[var(--ibp-navy-950)] text-white"
                      : "border border-[color:rgba(10,27,51,0.1)] bg-white text-[var(--ibp-ink)]",
                  ].join(" ")}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-[#B7791F] shadow-sm ring-1 ring-[color:rgba(10,27,51,0.1)]">
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
                <div className="rounded-xl border border-[color:rgba(10,27,51,0.1)] bg-white px-3.5 py-2.5 text-sm text-[color:rgba(11,18,32,0.6)]">
                  Thinking…
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-lg border border-[color:rgba(10,27,51,0.1)] bg-white p-3.5">
            <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#B7791F]" />
            <div>
              <p className="text-sm font-semibold text-[var(--ibp-ink)]">Ask a follow-up</p>
              <p className="mt-1 text-xs leading-5 text-[color:rgba(11,18,32,0.58)]">
                Compare choices, simplify the explanation, or clarify the Illinois rule being tested.
              </p>
            </div>
          </div>
        )}

        {/* Daily cap state for AI-enhanced follow-ups. */}
        {capMsg && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-[color:rgba(180,35,24,0.25)] bg-[color:rgba(180,35,24,0.06)] p-3 text-sm text-[#7f1d1d]">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <span>{capMsg}</span>
          </div>
        )}

        {/* Starter prompts */}
        {!blocked && thread.length === 0 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {STARTERS.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={STUDY.tutorPrompt}
                disabled={busy}
                onClick={() => send(s)}
                className="flex min-h-10 items-center justify-between gap-2 rounded-lg border border-[color:rgba(10,27,51,0.14)] bg-white px-3 py-2 text-left text-xs font-semibold text-[var(--ibp-ink)] transition-colors hover:border-[#B7791F] hover:bg-[#fbf8f0] disabled:opacity-50"
              >
                <span>{s}</span>
                <MessageCircle className="h-3.5 w-3.5 shrink-0 text-[#B7791F]" />
              </button>
            ))}
          </div>
        )}

        {/* Free-text box */}
        {!blocked && (
          <div className="mt-3 flex items-end gap-2 rounded-lg border border-[color:rgba(10,27,51,0.16)] bg-white p-2 focus-within:border-[var(--ibp-navy-950)] focus-within:ring-2 focus-within:ring-[color:rgba(214,162,30,0.16)]">
            <textarea
              data-testid={STUDY.tutorInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Ask about this question…"
              className="min-h-[44px] min-w-0 flex-1 resize-y border-0 bg-transparent px-2 py-1.5 text-sm text-[var(--ibp-ink)] outline-none"
            />
            <button
              type="button"
              title="Send question"
              aria-label="Send question"
              data-testid={STUDY.tutorSend}
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--ibp-gold-500)] text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <p className="flex items-start gap-2 border-t border-[color:rgba(10,27,51,0.1)] px-4 py-3 text-[11px] leading-5 text-[color:rgba(11,18,32,0.52)] sm:px-5">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#B7791F]" />
        Study guidance stays tied to the active question. It is not legal advice.
      </p>
    </div>
  );
}
